/**
 * The UI message catalogue: every user-facing string has an id here and `{name}` placeholders are
 * filled by the web app's t(). Translations are catalogues keyed the same way, one file per
 * language in messages/<code>.js (`export default { code, name, native, locale, messages }`), served
 * by GET /api/public/messages/:lang with the admin's wording overrides on top.
 */
export const MESSAGES = {
  'app.name': 'Octbill',

  'nav.dashboard': 'Dashboard',
  'nav.home': 'Home',
  'nav.services': 'Services',
  'nav.domains': 'Domains',
  'nav.invoices': 'Invoices',
  'nav.quotes': 'Quotes',
  'nav.tickets': 'Support',
  'nav.store': 'Store',
  'nav.cart': 'Cart',
  'nav.account': 'Account',
  'nav.affiliates': 'Affiliates',
  'nav.knowledgebase': 'Help articles',
  'nav.announcements': 'Announcements',
  'nav.network_status': 'Network status',
  'nav.admin': 'Admin',
  'nav.logout': 'Log out',
  'nav.menu': 'Menu',

  'service_status.pending': 'Pending',
  'service_status.active': 'Active',
  'service_status.suspended': 'Suspended',
  'service_status.terminated': 'Terminated',
  'service_status.cancelled': 'Cancelled',
  'service_status.fraud': 'Fraud',

  'invoice_status.draft': 'Draft',
  'invoice_status.unpaid': 'Unpaid',
  'invoice_status.paid': 'Paid',
  'invoice_status.cancelled': 'Cancelled',
  'invoice_status.refunded': 'Refunded',
  'invoice_status.collections': 'Collections',
  'invoice_status.payment_pending': 'Payment pending',

  // An Octail server's state, on a game-server service (ServerStatus in web/src/components/ui.jsx)
  'server_status.installing': 'Installing',
  'server_status.install_failed': 'Install failed',
  'server_status.suspended': 'Suspended',
  'server_status.ready': 'Ready',
  'server_status.running': 'Running',
  'server_status.starting': 'Starting',
  'server_status.stopping': 'Stopping',
  'server_status.offline': 'Offline',
  'server_status.sleeping': 'Sleeping',

  // Row action menus (web/src/components/row-actions.jsx)
  'row.more_actions': 'More actions',
  'row.copy': 'Copy',
  'row.actions': 'Actions',
  'row.cancel': 'Cancel',
  'row.more': 'More',

  // Logging in and signing up (auth module)
  'login.title': 'Log in',
  'login.email': 'Email address',
  'login.password': 'Password',
  'login.submit': 'Log in',
  'login.forgot': 'Forgot your password?',
  'login.no_account': 'New here?',
  'login.create_account': 'Create an account',
  'login.passkey': 'Log in with a passkey',
  'login.or': 'or',
  'login.maintenance': "We're doing maintenance.",
  'login.code.title': 'Two-factor authentication',
  'login.code.body': 'Enter the 6-digit code from your authenticator app.',
  'login.code.recovery.body': 'Enter one of the recovery codes you saved when you turned on two-factor authentication.',
  'login.code': 'Authenticator code',
  'login.code.recovery': 'Recovery code',
  'login.code.submit': 'Continue',
  'login.code.use_recovery': 'Use a recovery code instead',
  'login.code.use_app': 'Use the authenticator app instead',
  'register.title': 'Create your account',
  'register.lead': 'Order services, pay invoices and get support in one place.',
  'register.name_first': 'First name',
  'register.name_last': 'Last name',
  'register.company': 'Company',
  'register.country': 'Country',
  'register.phone': 'Phone',
  'register.password_help': 'At least {min} characters. A few unrelated words work well.',
  'register.terms': 'I accept the terms of service',
  'register.marketing': 'Send me news and offers by email',
  'register.submit': 'Create account',
  'register.have_account': 'Already have an account?',
  'forgot.title': 'Reset your password',
  'forgot.body': "Enter your email address and we'll send you a link to choose a new password.",
  'forgot.submit': 'Send the link',
  'forgot.sent': 'If an account uses that address, a link is on its way. It works for one hour.',
  'forgot.back': 'Back to log in',
  'reset.title': 'Choose a new password',
  'reset.password': 'New password',
  'reset.submit': 'Save password',
  'reset.done': 'Your password is saved. Log in with it now.',
  'reset.invalid': 'This link has expired or was already used.',
  'reset.again': 'Ask for a new link',
  'verify.title': 'Confirm your email address',
  'verify.working': 'Confirming…',
  'verify.done': 'Thanks: your email address is confirmed.',
  'verify.continue': 'Continue',
  'auth.admin_area': 'Admin area',
  'auth.admin': 'Admin',
  'auth.footer': 'Useful links',
  'auth.optional': 'optional',
  'register.closed': 'Sign-ups are closed',
  'register.closed.body': "New accounts can't be created right now.",
  'register.terms.read': 'read',
  'reset.invite_title': 'Set your password',
  'staff_login.title': 'Staff login',
  'staff_login.2fa.title': 'Set up two-factor authentication',
  'staff_login.2fa.body':
    'Your account needs a second step at login. Scan this code with an authenticator app, then enter the 6-digit code it shows.',
  'staff_login.2fa.key_help': "If you can't scan the code, type this key into the app.",
  'staff_login.2fa.submit': 'Turn on and log in',
  'staff_login.codes.title': 'Save your recovery codes',
  'staff_login.codes.done': 'I saved them, continue',

  // Account security (auth module)
  'security.title': 'Security',
  'security.lead': 'Password, two-factor authentication, passkeys and where you are signed in.',
  'security.password': 'Password',
  'security.password.current': 'Current password',
  'security.password.new': 'New password',
  'security.password.save': 'Change password',
  'security.password.saved': 'Password changed. Other sessions were signed out.',
  'security.2fa': 'Two-factor authentication',
  'security.2fa.on': 'On',
  'security.2fa.off': 'Off',
  'security.2fa.lead': 'Logging in also asks for a code from an authenticator app.',
  'security.2fa.setup': 'Turn on',
  'security.2fa.scan':
    'Scan this code with your authenticator app, or enter the key by hand, then type the 6-digit code it shows.',
  'security.2fa.key': 'Setup key',
  'security.2fa.code': 'Code from the app',
  'security.2fa.enable': 'Turn on two-factor authentication',
  'security.2fa.disable': 'Turn off',
  'security.2fa.recovery': 'Recovery codes',
  'security.2fa.recovery.left': '{count} recovery codes left',
  'security.2fa.recovery.new': 'Make new recovery codes',
  'security.2fa.recovery.save':
    'Save these recovery codes somewhere safe. Each works once if you lose your phone. They are shown only now.',
  'security.passkeys': 'Passkeys',
  'security.passkeys.lead': 'Log in with your fingerprint, face or a security key instead of a password.',
  'security.passkeys.add': 'Add a passkey',
  'security.passkeys.name': 'Name',
  'security.passkeys.none': 'No passkeys yet.',
  'security.passkeys.unsupported': "This browser doesn't support passkeys.",
  'security.sessions': 'Where you are signed in',
  'security.sessions.current': 'This browser',
  'security.sessions.revoke': 'Sign out',
  'security.sessions.revoke_others': 'Sign out everywhere else',
  'security.sessions.last_seen': 'Last active {when}',
  'security.sessions.browser': 'Browser',
  'security.sessions.address': 'Address',
  'security.sessions.signed_in': 'Signed in',
  'security.sessions.last_active': 'Last active',
  'security.sessions.sign_out': 'Sign out {name}',
  'security.qr': 'QR code for your authenticator app',
  'security.2fa.required': 'Your role requires two-factor authentication.',
  'security.2fa.recovery.download': 'Download',
  'security.unverified': 'Your email address is not confirmed yet.',
  'security.unverified.sent': 'A new link is on its way.',
  'security.unverified.resend': 'Send the link again',
  'security.impersonating': 'You are viewing this account as staff: security settings are read-only.',
  'security.passkeys.placeholder': 'Work laptop',
  'security.passkeys.added': 'Added',
  'security.passkeys.last_used': 'Last used',
  'security.passkeys.never': 'Never',
  'security.passkeys.rename': 'Name of {name}',
  'security.passkeys.remove': 'Remove {name}',
  'sudo.title': "Confirm it's you",
  'sudo.body': "This is a sensitive change. Enter your password; you won't be asked again for 15 minutes.",
  'sudo.password': 'Password',
  'sudo.code': 'Authenticator code',
  'sudo.passkey': 'Use a passkey',
  'sudo.confirm': 'Confirm',

  // Email history (mail module)
  'emails.title': 'Emails',
  'emails.lead': 'Every email we sent to your account.',
  'emails.none': 'No emails yet.',
  'emails.subject': 'Subject',
  'emails.to': 'To',
  'emails.sent': 'Sent',

  // Language (platform module)
  'language.title': 'Language',
  'language.lead': 'The language of the client area and of the emails we send you.',
  'language.label': 'Language',
  'language.saved': 'Language saved.',

  'nav.security': 'Security',
  'nav.emails': 'Emails',
  'nav.language': 'Language',

  // The client area: dashboard, services and the service page, invoices (provisioning, octail, invoices)
  'nav.support': 'Support',
  'nav.orders': 'Orders',
  'nav.contacts': 'Contacts',
  'nav.login': 'Log in',
  'nav.register': 'Create account',
  'nav.section.order': 'Order',
  'nav.section.help': 'Help',
  'nav.section.account': 'Account',
  'nav.section.pages': 'Information',
  'nav.badge.invoices.one': '{count} unpaid invoice',
  'nav.badge.invoices.other': '{count} unpaid invoices',
  'nav.badge.tickets.one': '{count} ticket waiting for you',
  'nav.badge.tickets.other': '{count} tickets waiting for you',
  'common.done': 'Done',
  'common.optional': 'optional',
  'cycle.free': 'Free',
  'cycle.onetime': 'One time',
  'cycle.monthly': 'Monthly',
  'cycle.quarterly': 'Quarterly',
  'cycle.semiannually': 'Every 6 months',
  'cycle.annually': 'Yearly',
  'cycle.biennially': 'Every 2 years',
  'cycle.triennially': 'Every 3 years',
  'dashboard.welcome': 'Welcome back',
  'dashboard.welcome_name': 'Welcome back, {name}',
  'dashboard.lead': 'Your servers, what is due and our news.',
  'dashboard.empty': 'Nothing here yet',
  'dashboard.servers': 'Your servers',
  'dashboard.view_all': 'View all',
  'dashboard.unpaid': 'Unpaid invoices',
  'dashboard.all_invoices': 'All invoices',
  'dashboard.due_total.one': 'due on {count} invoice',
  'dashboard.due_total.other': 'due on {count} invoices',
  'dashboard.due_on': 'due {date}',
  'dashboard.nothing_due': 'Nothing to pay. You’re all set.',
  'dashboard.tickets': 'Open tickets',
  'dashboard.no_tickets': 'No open tickets',
  'dashboard.open_ticket': 'Open a ticket',
  'dashboard.credit': 'Account credit',
  'dashboard.credit_lead': 'Used first on new invoices.',
  'dashboard.add_funds': 'Add funds',
  'dashboard.transactions': 'Transactions',
  'game.cpu': 'CPU',
  'game.memory': 'Memory',
  'game.disk': 'Disk',
  'game.of': '{used} of {limit}',
  'game.players': 'Players',
  'game.players_count': '{online}/{max} players',
  'game.copy_address': 'Copy address',
  'game.copy_address_of': 'Copy the address of {name}',
  'game.open_octail': 'Open in Octail',
  'game.open_panel': 'Open control panel',
  'game.open_in': '{panel}: {name}',
  'game.up_for': 'Up {duration}',
  'game.uptime_days': '{days} d {hours} h',
  'game.uptime_hours': '{hours} h {minutes} min',
  'game.power.start': 'Start',
  'game.power.stop': 'Stop',
  'game.power.restart': 'Restart',
  'game.power.group': 'Power for {name}',
  'game.power.action': '{action} {name}',
  'game.power.sent': '{action} sent to {name}.',
  'services.title': 'Services',
  'services.count.one': '{count} service',
  'services.count.other': '{count} services',
  'services.running.one': '{count} running',
  'services.running.other': '{count} running',
  'services.order': 'Order a server',
  'services.filters': 'Filter services',
  'services.search': 'Search services',
  'services.layout': 'Layout',
  'services.layout.cards': 'Cards',
  'services.layout.list': 'List',
  'services.filter.current': 'Current',
  'services.filter.active': 'Active',
  'services.filter.pending': 'Pending',
  'services.filter.suspended': 'Suspended',
  'services.filter.terminated': 'Terminated',
  'services.filter.cancelled': 'Cancelled',
  'services.filter.all': 'All',
  'services.empty': 'No servers yet',
  'services.empty_body': 'Servers you order show up here.',
  'services.no_match': 'Nothing matches',
  'services.clear': 'Clear filters',
  'services.service': 'Service',
  'services.status': 'Status',
  'services.address': 'Address',
  'services.price': 'Price',
  'services.product': 'Plan',
  'services.next_due': 'Next due',
  'services.next_due_on': 'Next due {date}',
  'services.pending_hint': 'Starts once paid',
  'services.cancelling': 'Cancelling',
  'services.addons': 'Addons',
  'services.manage': 'Manage',
  'services.actions_for': 'Actions for {name}',
  'services.tabs': 'Service sections',
  'services.tab.overview': 'Overview',
  'services.tab.billing': 'Billing',
  'services.tab.upgrade': 'Upgrade',
  'services.tab.addons': 'Addons',
  'services.tab.cancel': 'Cancel',
  'service.plan': 'Plan',
  'service.about': 'About this service',
  'service.started': 'Started',
  'service.ended': 'Ended',
  'service.username': 'Username',
  'service.panel_details': 'Details from the panel',
  'service.panel_actions': 'Server',
  'service.panel_down': 'The game panel isn’t answering right now. Try again in a minute.',
  'service.suspended': 'This service is suspended. Pay what is overdue or contact support.',
  'service.suspended_because': 'This service is suspended: {reason}',
  'service.suspended.pay': 'Unpaid invoices',
  'service.pending': 'We’re setting this up. It starts once the first invoice is paid.',
  'service.cancellation.now': 'Cancellation requested: it stops right away.',
  'service.cancellation.end': 'Cancellation requested: it stops on {date}.',
  'service.addon_of': 'An addon of',
  'service.password.title': 'Change password',
  'service.password.lead': 'The new password works on the game panel right away.',
  'service.password.new': 'New password',
  'service.password.help': 'At least 8 characters. Leave empty and we make a strong one.',
  'service.password.done': 'The password was changed.',
  'service.button.irreversible': 'This can’t be undone.',
  'service.reinstall.game': 'Game',
  'service.reinstall.current': 'Installed now',
  'service.reinstall.other': 'Another game',
  'service.reinstall.switch_note': 'A new server of this game replaces the old one. Its address may change.',
  'service.reinstall.understood': 'I understand that every file on the server is deleted',
  'service.billing.cycle': 'Billing cycle',
  'service.billing.first': 'First paid',
  'service.billing.invoices': 'Invoices',
  'service.upgrade.pick': 'Choose a plan',
  'service.upgrade.lead': 'You pay or get credit for the rest of this period.',
  'service.upgrade.current': 'Current plan',
  'service.upgrade.compare': 'Now and after',
  'service.upgrade.what': 'What',
  'service.upgrade.now': 'Now',
  'service.upgrade.after': 'After',
  'service.upgrade.pay_now': 'To pay today',
  'service.upgrade.credit': 'Credit for unused time',
  'service.upgrade.prorated': 'Prorated to your next due date.',
  'service.upgrade.continue': 'Change plan',
  'service.upgrade.continue_pay': 'Continue to payment',
  'service.upgrade.none': 'No other plans',
  'service.upgrade.none_body': 'This plan has nothing to move to yet.',
  'service.addons.none': 'No addons',
  'service.addons.none_for_plan': 'No addons for this plan',
  'service.addons.none_body': 'Addons we offer for it show up here.',
  'service.addons.unavailable': 'Addons aren’t available here',
  'service.addons.add': 'Add',
  'service.addons.add_named': 'Add {name}',
  'service.addons.setup': 'Setup',
  'service.addons.confirm': 'Add {name}?',
  'service.addons.today': 'To pay today',
  'service.addons.then': 'Then',
  'service.addons.prorated': 'Today covers the days until the service is next due; then it renews with it.',
  'service.addons.buy_pay': 'Continue to payment',
  'service.cancel.request': 'Request cancellation',
  'service.cancel.lead': 'Stop this service and its renewals.',
  'service.cancel.when': 'When',
  'service.cancel.end': 'At the end of the paid period ({date})',
  'service.cancel.now': 'Right away',
  'service.cancel.now_warning': 'The server and its files are deleted today.',
  'service.cancel.why': 'Why are you leaving?',
  'service.cancel.unavailable': 'Cancellations aren’t available online yet. Open a support ticket instead.',
  'octail.game_server': 'Game server',
  'octail.setting_up': 'Your server is being set up.',
  'octail.installing': 'The server is installing. This usually takes a few minutes.',
  'octail.install_failed': 'The install didn’t finish. We’ve been told and will fix it.',
  'octail.backups': 'Backups',
  'octail.databases': 'Databases',
  'octail.extra_ports': 'Extra ports',
  'octail.online_now': 'Online now',
  'octail.ports': 'Other ports',
  'invoices.title': 'Invoices',
  'invoices.lead': 'Everything we billed you, and what is still due.',
  'invoices.show': 'Show',
  'invoices.filter.all': 'All invoices',
  'invoices.filter.open': 'Unpaid',
  'invoices.filter.paid': 'Paid',
  'invoices.filter.cancelled': 'Cancelled',
  'invoices.filter.refunded': 'Refunded',
  'invoices.due_tile.one': 'Due ({count} invoice)',
  'invoices.due_tile.other': 'Due ({count} invoices)',
  'invoices.pay_together': 'Pay {count} invoices together',
  'invoices.empty': 'No invoices yet',
  'invoices.empty_body': 'Invoices for your orders and renewals show up here.',

  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.loading': 'Loading…',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.more': 'More actions',
  'common.close': 'Close',
  'common.error': 'Something went wrong.',
  'common.retry': 'Try again',
  'common.retrying': 'Trying again…',

  // The client shell, theme picker, cookie notice and accessibility statement (web/src/components)
  'shell.skip': 'Skip to content',
  'shell.sidebar': 'Sidebar',
  'shell.maintenance_on': 'Maintenance mode is on: only staff can log in.',
  'shell.maintenance_settings': 'Maintenance settings',
  'shell.impersonating': 'You are viewing the client area as {name}.',
  'shell.back_to_admin': 'Back to the admin area',
  'shell.accessibility': 'Accessibility',
  'shell.privacy': 'Privacy and cookies',
  'shell.site_menu': 'Site pages',
  // The home page visitors see at / (web/src/pages/Landing.jsx)
  'landing.title': 'Game servers, ready when you are',
  'landing.lead':
    'Rent a server for Minecraft, Rust, ARK and more. It is set up for you after you order, and you run it from the Octail game panel.',
  'landing.cta_store': 'Browse the store',
  'landing.cta_plans': 'See plans',
  'landing.from': 'Plans from {price} a month',
  'landing.games_title': 'Popular games',
  'landing.games_lead': 'Pick a game to see its plans.',
  'landing.all_games': 'All games and plans',
  'landing.features_title': 'Everything your server needs',
  'landing.feature.setup': 'Online in minutes',
  'landing.feature.setup.body': 'Your server is set up automatically once your order goes through.',
  'landing.feature.panel': 'The Octail game panel',
  'landing.feature.panel.body': 'Start and stop it, use the console, edit files and change settings in one place.',
  'landing.feature.locations': 'Choose your location',
  'landing.feature.locations.body': 'Pick the location closest to your players when you order.',
  'landing.feature.support': 'Help when you need it',
  'landing.feature.support.body': 'Open a support ticket from your account at any time.',
  'landing.start_title': 'Ready to play?',
  'theme.label': 'Theme',
  'theme.system': 'Same as this device',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'cookies.label': 'Cookies',
  'cookies.body':
    'We only use essential cookies: to keep you signed in and protect forms, to remember your language and to credit a referral. No tracking or advertising cookies.',
  'cookies.more': 'Privacy and cookies',
  'cookies.ok': 'OK',
  'a11y.title': 'Accessibility statement',
  'a11y.lead': 'Everyone should be able to order, pay and get support here, whatever they use.',
  'a11y.standard.title': 'Our standard',
  'a11y.standard.body':
    'We aim to meet the Web Content Accessibility Guidelines (WCAG) 2.1 at level AA on the store, the client area and the admin area.',
  'a11y.testing.title': 'How we check',
  'a11y.testing.item1':
    'Every release runs automated axe accessibility checks on the store, checkout, client area and admin pages.',
  'a11y.testing.item2':
    'Pages are used with the keyboard alone and with screen readers, in light and dark colours and zoomed to 200%.',
  'a11y.testing.item3': 'Text and controls meet the WCAG contrast ratios, and nothing relies on colour alone.',
  'a11y.limits.title': 'Known limitations',
  'a11y.limits.body':
    'Card payment fields and the anti-bot check are provided by third parties (Stripe and Cloudflare Turnstile) and follow their own accessibility work. Articles, announcements and product descriptions written by our team may not always meet the standard.',
  'a11y.report.title': 'Report a problem',
  'a11y.report.body':
    'If something is hard or impossible to use, tell us what you were trying to do and which page you were on. We reply within two working days and can help you with your order or account another way in the meantime.',
  'a11y.report.contact': 'Contact us',
  'a11y.report.email': 'Email {email}',

  // The offline page (web/public/offline.html; web/public/offline.js keeps a copy, see offline.test.js)
  'offline.page_title': 'Offline',
  'offline.title': 'You’re offline',
  'offline.body': 'This page needs a connection. Check your internet, then try again.',
  'offline.retry': 'Try again',

  // Shared components (web/src/components): buttons, help tips, pager, notifications, 404
  'common.working': 'Working…',
  'help.more_info': 'More info',
  'help.learn_more': 'Learn more',
  'help.page_docs': 'Read more about this page in the docs.',
  'pager.label': 'Pages',
  'pager.status': 'Page {page} of {pages} · {total} in all',
  'pager.previous': 'Previous',
  'pager.next': 'Next',
  'bell.title': 'Notifications',
  'bell.label_unread': 'Notifications, {count} unread',
  'bell.mark_all': 'Mark all as read',
  'bell.close': 'Close notifications',
  'bell.show': 'Show',
  'bell.all': 'All',
  'bell.unread': 'Unread',
  'bell.unread_count': 'Unread ({count})',
  'bell.none_unread': 'Nothing unread',
  'bell.empty': 'No notifications yet',
  'bell.unread_mark': '(unread)',
  'notfound.title': 'Page not found',
  'notfound.home': 'Go home',
  'notfound.body': 'The link may be old, or the page has moved.',
  'custom_page.login_hint': 'If this page is for clients, log in to read it.',
  'custom_page.updated': 'Updated {date}',

  // The store (orders module: store.jsx, common.jsx)
  'cycle_suffix.monthly': '/mo',
  'cycle_suffix.quarterly': '/3 mo',
  'cycle_suffix.semiannually': '/6 mo',
  'cycle_suffix.annually': '/yr',
  'cycle_suffix.biennially': '/2 yr',
  'cycle_suffix.triennially': '/3 yr',
  'cycle_every.monthly': 'every month',
  'cycle_every.quarterly': 'every 3 months',
  'cycle_every.semiannually': 'every 6 months',
  'cycle_every.annually': 'every year',
  'cycle_every.biennially': 'every 2 years',
  'cycle_every.triennially': 'every 3 years',
  'spec.memory': 'Memory',
  'spec.cpu': 'CPU',
  'spec.disk': 'Storage',
  'spec.slots': 'Players',
  'spec.vcpu': '{n} vCPU',
  'spec.players.one': '{count} player',
  'spec.players.other': '{count} players',
  'order_step.configure': 'Choose',
  'order_step.cart': 'Cart',
  'order_step.checkout': 'Details',
  'order_step.done': 'Pay',
  'order_status.pending': 'Pending',
  'order_status.active': 'Active',
  'order_status.cancelled': 'Cancelled',
  'order_status.fraud': 'Fraud',
  'cart.item.transfer': 'Transfer {domain}',
  'cart.item.register': 'Register {domain}',
  'cart.item.bundle': 'Bundle',
  'cart.item.product': 'Product',
  'store.not_on_sale': 'Not on sale in {currency}',
  'store.other_currency': 'Pick another currency in the store.',
  'store.free': 'Free',
  'store.from': 'From',
  'store.cart_count': 'Cart ({count})',
  'store.currency': 'Currency',
  'store.sr_title': 'Store:',
  'store.see_plans': 'See the plans',
  'store.empty': 'Nothing for sale yet',
  'store.empty_body': 'Check back soon.',
  'store.categories': 'Categories',
  'store.plans.one': '{count} plan',
  'store.plans.other': '{count} plans',
  'store.billing_cycle': 'Billing cycle',
  'store.one_time': 'one time',
  'store.billed_every': '{price} {every}',
  'store.save': 'save {pct}%',
  'store.save_cap': 'Save {pct}%',
  'store.billed_monthly': 'Billed monthly',
  'store.paid_once': 'Paid once',
  'store.setup': '{price} setup',
  'store.up_to': 'up to {value}',
  'store.and_more': '{list} and {count} more',
  'store.most_popular': 'Most popular',
  'store.games': 'Games',
  'store.locations': 'Locations',
  'store.order': 'Order',
  'store.order_name': 'Order {name}',
  'store.out_of_stock': 'Out of stock',
  'store.price': 'Price',
  'store.compare': 'Compare plans',
  'store.compare_scroll': 'Compare plans, scrolls sideways',
  'store.spec': 'Spec',
  'store.group_empty': 'No plans in this group yet',
  'store.bundle': 'Bundle',
  'store.has_room': 'Has room',
  'store.locations_full': 'Every location is full right now. Try again later or pick a smaller plan.',
  'store.each': '{price} each',
  'store.in_unit': 'In {unit}.',
  'store.choose': 'Choose…',
  'store.pay_as_you_go': 'Pay as you go',
  'store.your_price': 'Your price',
  'store.hide_details': 'Hide details',
  'store.show_details': 'Show details',
  'store.included': 'Included',
  'store.due_today': 'Due today',
  'store.then': 'Then {price}{suffix}',
  'store.free_trial': 'Free for {count} days',
  'store.free_trial.one': 'Free for {count} day',
  'store.free_trial.other': 'Free for {count} days',
  'store.trial_note':
    'The first {count} days are free if you haven’t had this trial before. The cart shows what applies to you.',
  'cart.trial': 'Free trial: {count} days. The first invoice, at the normal price, is due on {date}.',
  'cart.trial_card': 'It needs a saved card before it starts.',
  'cart.trial_used': 'You already had the free trial of this plan, so it is billed from today.',
  'invoice_status.written_off': 'Written off',
  'dispute.title': 'Dispute',
  'dispute.lead': 'Something wrong? We pause reminders while we look into it.',
  'dispute.start': 'Dispute this invoice',
  'dispute.reason': 'What is wrong?',
  'dispute.reason.wrong_amount': 'The amount is wrong',
  'dispute.reason.not_received': 'I did not get what I was billed for',
  'dispute.reason.cancelled': 'I had cancelled this service',
  'dispute.reason.duplicate': 'I was billed twice',
  'dispute.reason.other': 'Something else',
  'dispute.message': 'Tell us more',
  'dispute.message_help': 'What happened, and what you expected instead.',
  'dispute.send': 'Send',
  'dispute.open_note':
    'We are looking into it. Until then there are no reminders, late fees or suspensions for this invoice.',
  'dispute.ticket': 'Follow it in ticket #{number}',
  'dispute.status.open': 'Open',
  'dispute.status.upheld': 'Upheld',
  'dispute.status.rejected': 'Rejected',
  'credit_notes.title': 'Credit notes',
  'credit_notes.lead': 'Documents for refunds and credits on your invoices.',
  'credit_notes.number': 'Number',
  'credit_notes.reason': 'Reason',
  'credit_notes.none': 'No credit notes yet',
  'credit_notes.pdf_of': 'Download credit note {number} as a PDF',
  'store.add_to_cart': 'Add to cart',
  'store.tax_note': 'Tax and promo codes are worked out in the cart.',
  'store.only_left': 'Only {count} left',
  'store.what_you_get': 'What you get',
  'store.game_and_location': 'Game and location',
  'store.location': 'Location',
  'store.addons': 'Addons',
  'store.addons_lead': 'Optional extras, billed with this service.',
  'store.details': 'Details',
  'store.domain': 'Domain or hostname',
  'store.domain_help': 'The domain this service is for, like example.com.',
  'store.quantity': 'Quantity',

  // Cart, checkout and the order confirmation (orders module: cart.jsx)
  'cart.subtotal': 'Subtotal',
  'cart.discount': 'Discount',
  'cart.tax': 'Tax',
  'cart.tax2': 'Tax 2',
  'cart.tax_included': '{name} (included)',
  'cart.total_today': 'Total due today',
  'cart.then': 'Then {cycle}',
  'cart.reverse_charge': 'VAT reverse charge applies: no VAT is added.',
  'cart.remove': 'Remove',
  'cart.remove_name': 'Remove {name}',
  'cart.promo_code': 'Promo code',
  'cart.apply': 'Apply',
  'cart.years.one': '{count} year',
  'cart.years.other': '{count} years',
  'cart.addon': 'Addon',
  'cart.out_of_stock': 'Out of stock: remove it to check out.',
  'cart.add_domain': 'Add a domain',
  'cart.keep_shopping': 'Keep shopping',
  'cart.empty': 'Your cart is empty',
  'cart.empty_body': 'Products you add show up here.',
  'cart.go_store': 'Go to the store',
  'cart.steps': 'Order steps',
  'cart.items.one': '{count} item',
  'cart.items.other': '{count} items',
  'cart.summary': 'Summary',
  'cart.summary_label': 'Order summary',
  'cart.check_out': 'Check out',
  'checkout.lead': 'Review your order, then pay on the next page.',
  'checkout.account': 'Your account',
  'checkout.account_lead': 'New here? We create your account with the order.',
  'checkout.account_mode': 'Account',
  'checkout.new_here': 'I am new here',
  'checkout.have_account': 'I have an account',
  'checkout.two_step': 'Use two-step login? {link}, then come back.',
  'checkout.log_in_first': 'Log in first',
  'checkout.first_name': 'First name',
  'checkout.last_name': 'Last name',
  'checkout.password_help': 'At least 8 characters. A few unrelated words work well.',
  'checkout.company': 'Company',
  'checkout.country': 'Country',
  'checkout.address': 'Address',
  'checkout.city': 'City',
  'checkout.postcode': 'Postcode',
  'checkout.vat': 'VAT number',
  'checkout.vat_help': 'Businesses in the EU: for the reverse charge.',
  'checkout.marketing': 'Send me news and offers by email',
  'checkout.ordering_as': 'Ordering as {name} ({email}).',
  'checkout.payment_method': 'Payment method',
  'checkout.gateway.card': 'Pay securely on the next page.',
  'checkout.gateway.manual': 'You get the payment details with the invoice.',
  'checkout.gateway.credit': 'Uses the credit on your account.',
  'checkout.pay_later': 'You can pay the invoice once the order is placed.',
  'checkout.notes': 'Notes',
  'checkout.notes_lead': 'Anything we should know about this order.',
  'checkout.notes_label': 'Notes for us',
  'checkout.accept_terms': 'I accept the {terms}',
  'checkout.terms': 'terms of service',
  'checkout.place_order': 'Place order',
  'checkout.back_to_cart': 'Back to the cart',
  'order_done.title': 'Order placed',
  'order_done.number': 'Order #{number}',
  'order_done.pay': 'Pay {amount} and we set everything up straight away.',
  'order_done.paid': 'It is paid. We are setting it up now and email you when it is ready.',
  'order_done.setting_up': 'We are setting it up now and email you when it is ready.',
  'order_done.pay_invoice': 'Pay invoice',
  'order_done.view_invoice': 'View invoice',
  'order_done.services': 'Your services',
  'order_done.in_order': 'In this order',
  'order_done.setting_up_badge': 'Setting up',
  'order_done.details': 'Order details',

  // The client area order history (orders module: client.jsx)
  'orders.lead': 'Everything you ordered, and the invoice for each.',
  'orders.order_something': 'Order something',
  'orders.caption': 'Your orders',
  'orders.empty': 'No orders yet',
  'orders.empty_body': 'Orders you place in the store show up here.',
  'orders.order': 'Order',
  'orders.placed': 'Placed',
  'orders.status': 'Status',
  'orders.total': 'Total',
  'orders.invoice': 'Invoice',
  'orders.none': 'None',
  'orders.services_caption': 'Services in this order',
  'orders.service': 'Service',
  'orders.billing': 'Billing',
  'orders.domain': 'Domain',
  'orders.domains_caption': 'Domains in this order',

  // Invoices, quotes, transactions and add funds in the client area (invoices module)
  'invoices.overdue': 'Overdue',
  'invoices.invoice_number': 'Invoice {number}',
  'invoices.draft_title': 'Draft invoice',
  'invoices.proforma_title': 'Proforma invoice',
  'invoices.proforma': 'Proforma',
  'invoices.subtotal_net': 'Subtotal (net)',
  'invoices.credit_applied': 'Credit applied',
  'invoices.balance_due': 'Balance due',
  'invoices.reverse_charge':
    'Reverse charge: VAT to be accounted for by the recipient (Article 196, Council Directive 2006/112/EC).',
  'invoices.filters': 'Invoice filters',
  'invoices.date': 'Date',
  'invoices.due': 'Due',
  'invoices.issued_due': 'Issued {issued} · due {due}',
  'invoices.processing': 'Your payment is being processed. This page updates once it clears.',
  'invoices.payments': 'Payments',
  'invoices.paid_on': 'Paid on {date}. Thank you!',
  'invoices.lines': 'Lines',
  'invoices.description': 'Description',
  'invoices.period': '{from} to {to}',
  'invoices.amount': 'Amount',
  'invoices.type': 'Type',
  'invoices.method': 'Method',
  'tx_type.payment': 'Payment',
  'tx_type.refund': 'Refund',
  'tx_type.chargeback': 'Chargeback',
  'gateway.stripe': 'Card',
  'gateway.banktransfer': 'Bank transfer',
  'gateway.manual': 'Manual',
  'gateway.credit': 'Account credit',
  'gateway.paypal': 'PayPal',
  'transactions.lead': 'Payments, refunds and changes to your account credit.',
  'transactions.payments': 'Payments and refunds',
  'transactions.empty': 'No transactions yet',
  'transactions.credit_history': 'Credit history',
  'transactions.change': 'Change',
  'transactions.balance': 'Balance',
  'funds.bad_amount': 'Enter an amount, like 25.00.',
  'funds.lead': 'Credit pays your next invoices automatically.',
  'funds.unavailable': 'Adding funds is not available right now.',
  'funds.your_credit': 'Your credit: {amount}',
  'funds.amount': 'Amount ({currency})',
  'funds.between': 'Between {min} and {max}.',
  'funds.continue': 'Continue to payment',
  'quote_status.draft': 'Draft',
  'quote_status.delivered': 'Sent',
  'quote_status.accepted': 'Accepted',
  'quote_status.declined': 'Declined',
  'quote_status.expired': 'Expired',
  'quotes.lead': 'Proposals we prepared for you.',
  'quotes.quote': 'Quote',
  'quotes.title': 'Quote {number}',
  'quotes.subject': 'Subject',
  'quotes.valid_until': 'Valid until',
  'quotes.empty': 'No quotes',
  'quotes.empty_body': 'Ask us for a quote and it shows up here.',
  'quotes.lines': 'Quote lines',
  'quotes.qty': 'Qty',
  'quotes.unit_price': 'Unit price',
  'quotes.answer': 'Your answer',
  'quotes.valid_note': 'This quote is valid until {date}.',
  'quotes.accept': 'Accept quote',
  'quotes.decline': 'Decline',
  'quotes.invoiced': 'This quote was turned into an invoice.',
  'public.bad_link': 'This link doesn’t work',
  'public.bad_invoice_link': 'Open the invoice from your account, or ask us for a new link.',
  'public.bad_quote_link': 'Ask us for a new copy of the quote.',
  'public.invoice_lead': '{name} · due {due}',
  'public.invoice_paid': 'This invoice is paid. Thank you!',
  'public.quote_accepted': 'Thank you for accepting. We’ll send the invoice shortly.',
  'public.quote_declined': 'You declined this quote.',

  // Paying an invoice and saved payment methods (payments module)
  'pay.card_ending': '{brand} ending {last4}',
  'pay.card_expires': '{card}, expires {expiry}',
  'pay.received': 'Payment received. Thank you!',
  'pay.processing': 'Your payment is processing. We’ll email you when it clears.',
  'pay.failed': 'The payment was not completed. Try again or use another method.',
  'pay.paypal_processing': 'PayPal is still processing your payment. We’ll email you when it clears.',
  'pay.paypal_cancelled': 'The PayPal payment was cancelled. Nothing was charged.',
  'pay.bank_declined': 'Your bank did not confirm the payment.',
  'pay.credit_partial': 'Credit applied. Pay the rest with another method.',
  'pay.credit_paid': 'Paid with your account credit.',
  'pay.title': 'Pay this invoice',
  'pay.amount_due': 'Amount due: {amount}',
  'pay.loading_options': 'Loading payment options',
  'pay.none': 'No payment method is set up yet. Contact us to pay this invoice.',
  'pay.pay_with': 'Pay with',
  'pay.default': '(default)',
  'pay.new_method': 'A new card or another payment method',
  'pay.pay_amount': 'Pay {amount}',
  'pay.save_method': 'Save this payment method for renewals',
  'pay.loading_form': 'Loading the secure payment form',
  'pay.paypal_confirming': 'Confirming your PayPal payment',
  'pay.paypal_loading': 'Loading PayPal',
  'pay.paypal_no_link': 'PayPal did not return an approval link.',
  'pay.bank_reference': 'Use {reference} as the payment reference. We mark the invoice paid when the money arrives.',
  'pay.credit_have': 'You have {amount} of credit.',
  'pay.credit_part': 'It pays part of this invoice; pay the rest another way.',
  'pay.credit_covers': 'It covers this invoice.',
  'pay.use_credit': 'Use my credit',
  'pay.not_through': 'The payment did not go through.',
  'pay.pay_now': 'Pay now',
  'pay.not_saved': 'The card could not be saved.',
  'pay.save_card': 'Save card',
  'methods.saved': 'Card saved.',
  'methods.title': 'Payment methods',
  'methods.lead': 'Saved cards pay renewals automatically on their due date.',
  'methods.add': 'Add a card',
  'methods.loading_form': 'Loading the secure card form',
  'methods.caption': 'Saved payment methods',
  'methods.expires': 'Expires',
  'methods.default': 'Default',
  'methods.actions_for': 'Actions for {card}',
  'methods.make_default': 'Make default',
  'methods.remove_title': 'Remove this card?',
  'methods.remove_body': 'Renewals won’t be charged to it any more.',
  'methods.empty': 'No saved cards',
  'methods.empty_body': 'Add a card, or tick “save” when you next pay an invoice.',
  'methods.no_stripe': 'Card payments are not available yet.',
  'methods.method': 'Payment method',
  'methods.paypal_hint': 'Add a PayPal account here, or tick “save” when you next pay an invoice with PayPal.',
  'methods.add_paypal': 'Add PayPal account',
  'methods.paypal_lead': 'Approve it in PayPal. Renewals can then be charged to it automatically.',
  'methods.paypal_saved': 'PayPal account saved.',
  'methods.paypal_confirming': 'Saving your PayPal account',
  'methods.paypal_cancelled': 'Adding the PayPal account was cancelled.',
  'methods.paypal_blocked': 'PayPal’s buttons didn’t load here. You can still approve on PayPal’s site.',
  'methods.paypal_continue': 'Continue to PayPal',
  'methods.remove_paypal_title': 'Remove this PayPal account?',
  'pay.paypal_account': 'PayPal account {email}',
  'pay.paypal_saved_accounts': 'Saved PayPal accounts',
  'pay.paypal_new': 'Another PayPal account',
  'pay.paypal_save': 'Save this PayPal account for automatic renewals',
  'pay.paypal_saved': 'Payment received. Your PayPal account is saved for renewals.',

  // Domains in the client area and the domain search (domains module)
  'common.on': 'On',
  'common.off': 'Off',
  'domain_status.pending': 'Pending',
  'domain_status.pending_transfer': 'Transfer pending',
  'domain_status.active': 'Active',
  'domain_status.expired': 'Expired',
  'domain_status.cancelled': 'Cancelled',
  'domain_status.transferred_away': 'Transferred away',
  'domain_status.fraud': 'Fraud',
  'dsearch.title': 'Find a domain',
  'dsearch.lead': 'Search for a name, then register it or transfer it to us.',
  'dsearch.unsupported': 'We don’t sell this extension.',
  'dsearch.unknown': 'We couldn’t check it right now.',
  'dsearch.premium': 'Available (premium name: ask us for a price)',
  'dsearch.available': 'Available',
  'dsearch.taken': 'Taken',
  'dsearch.taken_long': 'Taken: you can transfer it to us if it’s yours',
  'dsearch.first_year': 'first year',
  'dsearch.in_cart': 'In your cart',
  'dsearch.transfer': 'Transfer',
  'dsearch.name': 'Domain name',
  'dsearch.placeholder': 'yourname.com',
  'dsearch.search': 'Search',
  'dsearch.results': 'Results',
  'dsearch.epp': 'Auth (EPP) code for {domain}',
  'dsearch.add_transfer': 'Add transfer',
  'dsearch.go_cart': 'Go to your cart',
  'dsearch.prices': 'Prices',
  'dsearch.prices_lead': 'Per year, before tax.',
  'dsearch.prices_caption': 'Domain prices',
  'dsearch.extension': 'Extension',
  'dsearch.register': 'Register',
  'dsearch.renew': 'Renew',
  'dsearch.none': 'No domains on sale yet.',
  'domains.lead': 'Your domain names, when they renew, and their settings.',
  'domains.caption': 'Your domains',
  'domains.auto_renew': 'Auto-renew',
  'domains.renews_at': 'Renews at',
  'domains.empty': 'No domains yet',
  'domains.empty_body': 'Register a new name or transfer one you already own.',
  'domains.nameservers': 'Nameservers',
  'domains.nameservers_lead': 'Where the domain’s DNS is hosted. At least two.',
  'domains.nameserver': 'Nameserver {n}',
  'domains.add_another': 'Add another',
  'domains.dns': 'DNS records',
  'domains.dns_lead': 'Changes go live in a minute or two.',
  'domains.record_name': 'Name',
  'domains.value': 'Value',
  'domains.ttl_auto': 'Auto',
  'domains.delete_record': 'Delete {record}',
  'domains.no_records': 'No records yet.',
  'domains.add_record': 'Add a record',
  'domains.at_for': '@ for {domain}',
  'domains.priority': 'Priority',
  'domains.ttl_help': '1 = automatic.',
  'domains.proxied': 'Proxy through Cloudflare (web traffic only, not game servers)',
  'domains.add_record_button': 'Add record',
  'domains.registrant': 'Registrant contact',
  'domains.registrant_lead': 'The owner of the domain, as the registry has it.',
  'domains.contact.name': 'Name',
  'domains.contact.organization': 'Organisation',
  'domains.contact.email': 'Email',
  'domains.contact.phone': 'Phone',
  'domains.contact.street': 'Street',
  'domains.contact.city': 'City',
  'domains.contact.state': 'State or region',
  'domains.contact.postal_code': 'Postcode',
  'domains.contact.country': 'Country code',
  'domains.save_contact': 'Save contact',
  'domains.expires_on': 'Expires {date}',
  'domains.expired_note': 'This domain has expired. Pay the renewal invoice to keep it.',
  'domains.pending_note':
    'We’re setting this domain up. It becomes active once the invoice is paid and the registry confirms.',
  'domains.pending_changes': 'Changes waiting for us: {list}.',
  'domains.change.register': 'registration',
  'domains.change.transfer': 'transfer',
  'domains.change.renew': 'renewal',
  'domains.change.nameservers': 'nameservers',
  'domains.change.lock': 'registrar lock',
  'domains.change.contacts': 'contacts',
  'domains.change.epp': 'auth code',
  'domains.overview': 'Overview',
  'domains.registered': 'Registered',
  'domains.id_protection': 'ID protection',
  'domains.dns_management': 'DNS management',
  'domains.renewal_security': 'Renewal and security',
  'domains.auto_renew_label': 'Renew automatically (we send the invoice before it expires)',
  'domains.lock_label': 'Registrar lock (stops transfers away)',
  'domains.get_code': 'Get auth code',
  'domains.unlock_first': 'Unlock the domain first.',
  'domains.copy_code': 'Copy auth code',
  'domains.contacts': 'Contacts',
  'domains.contacts_lead': 'The registrant (owner) on record.',
  'domains.edit': 'Edit',

  // Support tickets in the client area and for guests (support module)
  'tickets.attachments': 'Attachments',
  'tickets.attachments_help': 'Up to {max} files: images, PDFs, logs, zips.',
  'tickets.note_from': 'Internal note from {name}',
  'tickets.message_from': 'Message from {name}',
  'tickets.system': 'System',
  'tickets.unknown': 'Unknown',
  'tickets.staff': 'Staff',
  'tickets.internal_note': 'Internal note',
  'tickets.by_email': 'by email',
  'tickets.lead': 'Your tickets with us.',
  'tickets.all': 'All tickets',
  'tickets.open': 'Open',
  'tickets.closed': 'Closed',
  'tickets.caption': 'Your tickets',
  'tickets.last_reply': 'Last reply',
  'tickets.empty': 'No tickets',
  'tickets.empty_body': 'Questions or problems: we’re here to help.',
  'tickets.new_lead': 'Tell us what’s going on. We reply by email and here.',
  'tickets.department': 'Department',
  'ticket_priority.low': 'Low',
  'ticket_priority.medium': 'Medium',
  'ticket_priority.high': 'High',
  'ticket_priority.urgent': 'Urgent',
  'tickets.about_service': 'About a service',
  'tickets.message': 'Message',
  'tickets.message_help': 'Markdown works. Don’t send passwords.',
  'tickets.open_button': 'Open ticket',
  'tickets.rated': 'You rated this {n} out of 5. Thank you!',
  'tickets.how_did_we_do': 'How did we do?',
  'tickets.stars': '{n} out of 5',
  'tickets.comment': 'Comment',
  'tickets.send_rating': 'Send rating',
  'tickets.your_reply': 'Your reply',
  'tickets.send_reply': 'Send reply',
  'tickets.close': 'Close ticket',
  'tickets.about': 'About {service}',
  'tickets.a_service': 'a service',
  'tickets.reply': 'Reply',
  'tickets.closed_note': 'This ticket is closed. A reply opens it again.',
  'tickets.guest_login': 'Log in to contact support',
  'tickets.guest_login_body': 'We answer tickets from clients with an account.',
  'tickets.opened': 'Ticket opened',
  'tickets.opened_body': 'Ticket #{number} is open.',
  'tickets.contact': 'Contact support',
  'tickets.contact_lead': 'No account needed. We’ll email you a link to follow the ticket.',
  'tickets.your_name': 'Your name',
  'tickets.send': 'Send',

  // Help articles, announcements, downloads, network status and contact (content module)
  'kb.search_label': 'Search help articles',
  'kb.search_placeholder': 'Search articles',
  'kb.no_articles': 'No articles here yet',
  'kb.lead': 'Guides and answers for your services.',
  'kb.results_for': 'Results for “{q}”',
  'kb.articles.one': '{count} article',
  'kb.articles.other': '{count} articles',
  'kb.no_match': 'Nothing matches. Try other words, or browse the categories.',
  'kb.browse': 'Browse all categories',
  'kb.empty': 'No help articles yet',
  'kb.empty_body': 'Guides show up here once the team publishes them.',
  'kb.breadcrumb': 'Breadcrumb',
  'kb.updated': 'Updated {date}',
  'kb.also_in': 'Also in:',
  'kb.thanks': 'Thanks for telling us.',
  'kb.helpful_q': 'Was this helpful?',
  'kb.yes': 'Yes',
  'kb.no': 'No',
  'kb.found_helpful.one': '{count} person found this helpful',
  'kb.found_helpful.other': '{count} found this helpful',
  'kb.about_article': 'About this article',
  'kb.on_this_page': 'On this page',
  'kb.related': 'Related',
  'kb.still_stuck': 'Still stuck? {link}.',
  'kb.more_in_category': 'More in this category',
  'news.lead': 'News, changes and planned work.',
  'news.rss': 'RSS feed',
  'news.pinned': 'Pinned',
  'news.read_more': 'Read the whole announcement',
  'news.newer': 'Newer',
  'news.older': 'Older',
  'news.empty': 'No announcements yet',
  'news.empty_body': 'News and planned work show up here.',
  'news.nothing_new': 'Nothing new.',
  'downloads.title': 'Downloads',
  'downloads.lead': 'Tools, launchers and files for your services.',
  'downloads.with_products': 'Comes with some products',
  'downloads.log_in': 'Log in to download',
  'downloads.download': 'Download',
  'downloads.empty': 'No downloads yet',
  'status.overall.operational': 'All systems are working normally.',
  'status.overall.degraded': 'Some services are degraded.',
  'status.overall.partial_outage': 'Some services are down.',
  'status.overall.major_outage': 'A major outage is in progress.',
  'status.maintenance': 'Maintenance',
  'status.incident': 'Incident',
  'status.affects': 'Affects: {list}',
  'status.lead': 'Live incidents and planned maintenance.',
  'status.now': 'Now',
  'status.planned': 'Planned maintenance',
  'status.past': 'Past incidents',
  'status.nothing_recent': 'Nothing recently.',
  'incident_status.investigating': 'Investigating',
  'incident_status.identified': 'Identified',
  'incident_status.monitoring': 'Monitoring',
  'incident_status.resolved': 'Resolved',
  'incident_status.scheduled': 'Scheduled',
  'incident_status.in_progress': 'In progress',
  'incident_status.completed': 'Completed',
  'contact.sent': 'Message sent',
  'contact.title': 'Contact us',
  'contact.lead': 'Questions before you order, or anything else.',

  // The client account pages: profile, contacts and credit (clients module)
  'client_status.active': 'Active',
  'client_status.inactive': 'Inactive',
  'client_status.closed': 'Closed',
  'profile.name_first': 'First name',
  'profile.name_last': 'Last name',
  'profile.email': 'Email address',
  'profile.company': 'Company',
  'profile.address1': 'Address',
  'profile.address2': 'Address line 2',
  'profile.city': 'City',
  'profile.state': 'State or region',
  'profile.postcode': 'Postcode',
  'profile.country': 'Country',
  'profile.phone': 'Phone',
  'profile.tax_id': 'VAT number',
  'profile.tax_id_help': 'EU businesses: checked with VIES for the reverse charge.',
  'email_pref.general': 'General emails',
  'email_pref.invoices': 'Invoices and payments',
  'email_pref.services': 'Services',
  'email_pref.domains': 'Domains',
  'email_pref.support': 'Support tickets',
  'email_pref.affiliates': 'Affiliate program',
  'contact_perm.profile': 'Edit the account profile',
  'contact_perm.contacts': 'Manage contacts',
  'contact_perm.services': 'See services',
  'contact_perm.services.manage': 'Manage services (panel login, buttons)',
  'contact_perm.domains': 'See domains',
  'contact_perm.domains.manage': 'Manage domains',
  'contact_perm.invoices': 'See invoices',
  'contact_perm.payments': 'Pay invoices and manage cards',
  'contact_perm.quotes': 'See and accept quotes',
  'contact_perm.tickets': 'Open and answer tickets',
  'contact_perm.orders': 'Place orders',
  'contact_perm.affiliates': 'See the affiliate account',
  'contact_perm.emails': 'See sent emails',
  'contact_perm.api': 'Use API keys and webhooks',
  'nav.api_keys': 'API keys',
  'nav.webhooks': 'Webhooks',
  'client_api.keys_lead': 'Let your own scripts and bots use your account.',
  'client_api.new_key': 'New API key',
  'client_api.off': 'API keys and webhooks are switched off on this site.',
  'client_api.empty': 'No API keys yet',
  'client_api.empty_body': 'A key lets a script read your services, invoices or tickets, or press power buttons.',
  'client_api.copy_now': 'Copy the key for {name} now: it is shown only once.',
  'client_api.copy': 'Copy the API key',
  'client_api.use_as': 'Send it in the header Authorization: Bearer {prefix}…',
  'client_api.name': 'Name',
  'client_api.name_help': 'What uses it, e.g. your Discord bot.',
  'client_api.scopes': 'What it may do',
  'client_api.scope.services_read': 'See services',
  'client_api.scope.services_power': 'Start, stop and restart servers',
  'client_api.scope.invoices_read': 'See invoices',
  'client_api.scope.tickets_read': 'See tickets',
  'client_api.scope.tickets_write': 'Open and answer tickets',
  'client_api.scope.domains_read': 'See domains',
  'client_api.scope.domains_dns': 'Manage DNS records',
  'client_api.ips': 'Allowed addresses',
  'client_api.ips_help': 'IP addresses or ranges, one per line. Empty: from anywhere.',
  'client_api.expires': 'Expires',
  'client_api.create': 'Create key',
  'client_api.key': 'Key',
  'client_api.created_by': 'Made by',
  'client_api.owner': 'Account owner',
  'client_api.last_used': 'Last used',
  'client_api.used_from': '{when} from {ip}',
  'client_api.never': 'Never',
  'client_api.status': 'Status',
  'client_api.status.active': 'Active',
  'client_api.status.revoked': 'Revoked',
  'client_api.status.expired': 'Expired',
  'client_api.until': 'until {date}',
  'client_api.actions_for': 'Actions for the key {name}',
  'client_api.revoke_menu': 'Revoke…',
  'client_api.revoke': 'Revoke',
  'client_api.revoke_title': 'Revoke {name}?',
  'client_api.revoke_body': 'Scripts using this key stop working at once.',
  'client_api.docs_lead': 'Which endpoints each permission opens is in the',
  'client_api.docs': 'API reference (OpenAPI)',
  'client_hooks.lead': 'Get a signed POST on your server when something happens in your account.',
  'client_hooks.new': 'New webhook',
  'client_hooks.add': 'Add webhook',
  'client_hooks.edit': 'Edit',
  'client_hooks.edit_title': 'Edit the webhook',
  'client_hooks.empty': 'No webhooks yet',
  'client_hooks.empty_body':
    'Tell your server about new invoices, payments, server status, ticket replies and expiring domains.',
  'client_hooks.url': 'URL',
  'client_hooks.url_help': 'A public https:// address on your server.',
  'client_hooks.description': 'Description',
  'client_hooks.events': 'Events',
  'client_hooks.event.invoice_created': 'A new invoice',
  'client_hooks.event.invoice_paid': 'An invoice is paid',
  'client_hooks.event.service_active': 'A service is ready or back online',
  'client_hooks.event.service_suspended': 'A service is suspended',
  'client_hooks.event.service_terminated': 'A service is terminated',
  'client_hooks.event.ticket_replied': 'We replied to a ticket',
  'client_hooks.event.domain_expiring': 'A domain expires soon',
  'client_hooks.event.ping': 'Test',
  'client_hooks.enabled': 'Send events to this URL',
  'client_hooks.secret_now': 'Copy the signing secret for {url} now: it is shown only once.',
  'client_hooks.copy_secret': 'Copy the signing secret',
  'client_hooks.verify': 'Check the Octbill-Signature header: t=…,v1= is an HMAC-SHA256 of “t.body” with this secret.',
  'client_hooks.test': 'Send a test',
  'client_hooks.test_ok': 'Your server answered HTTP {status}.',
  'client_hooks.test_failed': 'The test did not arrive: {error}',
  'client_hooks.deliveries': 'Delivery log',
  'client_hooks.deliveries_title': 'Deliveries to {url}',
  'client_hooks.no_deliveries': 'Nothing sent yet.',
  'client_hooks.event_col': 'Event',
  'client_hooks.when': 'When',
  'client_hooks.details': 'Details',
  'client_hooks.payload': 'What we sent',
  'client_hooks.response': 'Your server’s answer',
  'client_hooks.send_again': 'Send again',
  'client_hooks.status.success': 'Delivered',
  'client_hooks.status.failed': 'Failed',
  'client_hooks.status.retrying': 'Retrying',
  'client_hooks.status.pending': 'Waiting',
  'client_hooks.last': 'Last delivery',
  'client_hooks.none_yet': 'None yet',
  'client_hooks.paused': 'Paused',
  'client_hooks.actions_for': 'Actions for the webhook to {url}',
  'client_hooks.new_secret_menu': 'New signing secret…',
  'client_hooks.new_secret': 'Make a new secret',
  'client_hooks.new_secret_title': 'Make a new signing secret?',
  'client_hooks.new_secret_body': 'The old one stops working at once, so update your server too.',
  'client_hooks.delete_menu': 'Delete…',
  'client_hooks.delete_title': 'Delete the webhook to {url}?',
  'client_hooks.delete_body': 'Its delivery log goes too.',
  'client_hooks.retries': 'A failed delivery is retried for 24 hours. The log keeps 30 days.',
  'account.accept': 'Accept',
  'account.tos_changed': 'Our terms of service changed. Please read and accept them.',
  'account.tos_changed_version': 'Our terms of service changed (version {version}). Please read and accept them.',
  'account.marketing': 'Marketing email',
  'account.marketing_lead': 'News and offers. Invoices and service emails always arrive.',
  'account.lead': 'Your details appear on invoices.',
  'account.download_data': 'Download my data',
  'account.profile': 'Profile',
  'account.locked': 'Contact us to change: {list}.',
  'account.vat_checked': 'Checked',
  'account.vat_unchecked': 'Not checked',
  'account.saved': 'Saved.',
  'account.vat_failed': 'The VAT number could not be confirmed with VIES.',
  'account.credit_lead': 'Credit pays new invoices first.',
  'account.no_credit': 'No credit yet',
  'contacts.emails_they_get': 'Emails they get',
  'contacts.login': 'Login',
  'contacts.let_log_in': 'Let them log in to this account',
  'contacts.new_password': 'New password',
  'contacts.keep_password': 'leave empty to keep it',
  'contacts.may_do': 'What they may do when logged in:',
  'contacts.add': 'Add contact',
  'contacts.lead': 'People who get emails for this account, or log in with their own password.',
  'contacts.empty': 'No contacts yet',
  'contacts.empty_body': 'Add a colleague who should get invoices or open tickets.',
  'contacts.can_log_in': 'Can log in',
  'contacts.emails_only': 'Emails only',
  'contacts.edit_name': 'Edit {name}',
  'contacts.add_title': 'Add a contact',
  'contacts.edit_title': 'Edit contact',

  // The affiliate page and the unsubscribe page (growth module)
  'aff_status.pending': 'Pending',
  'aff_status.available': 'Available',
  'aff_status.requested': 'Requested',
  'aff_status.paid': 'Paid',
  'aff_status.reversed': 'Reversed',
  'aff_status.approved': 'Approved',
  'aff_status.rejected': 'Rejected',
  'aff.rate_fixed': '{amount} per payment',
  'aff.rate_pct': '{pct} of each payment',
  'aff.commissions': 'Commissions',
  'aff.for': 'For',
  'aff.available_on': 'Available',
  'aff.no_commissions': 'No commissions yet',
  'aff.payouts': 'Payouts',
  'aff.requested': 'Requested',
  'aff.paid_as': 'Paid as',
  'aff.by_hand': 'By hand',
  'aff.no_payouts': 'No payouts yet',
  'aff.lead': 'Earn commission when people you refer pay for their services.',
  'aff.closed': 'The affiliate programme is closed',
  'aff.join': 'Join the programme',
  'aff.terms_recurring.one':
    'You earn {rate}, on renewals too. Commission can be paid out {count} day after the payment.',
  'aff.terms_recurring.other':
    'You earn {rate}, on renewals too. Commission can be paid out {count} days after the payment.',
  'aff.terms_first.one':
    'You earn {rate} (first payment of each service). Commission can be paid out {count} day after the payment.',
  'aff.terms_first.other':
    'You earn {rate} (first payment of each service). Commission can be paid out {count} days after the payment.',
  'aff.get_link': 'Get my referral link',
  'aff.ask_us': 'Ask us to open an affiliate account for you.',
  'aff.paused': 'Your affiliate account is paused. New referrals don’t earn commission.',
  'aff.your_link': 'Your referral link',
  'aff.you_earn': 'You earn {rate}.',
  'aff.copy_link': 'Copy your referral link',
  'aff.to_hint': 'Add {param} to send people to a page of your choice.',
  'aff.visitors': 'Visitors',
  'aff.signups': 'Sign-ups',
  'aff.pending': 'Pending ({currency})',
  'aff.available': 'Available ({currency})',
  'aff.paid_out': 'Paid out ({currency})',
  'aff.request_payout': 'Request a payout',
  'aff.pay_out': 'Pay out {amount}',
  'aff.payout_min': 'Payouts start at {amount} available.',
  'unsub.bad_link': 'That link didn’t work',
  'unsub.done': 'You’re unsubscribed',
  'unsub.bad_link_body': 'Change your email preferences in your account instead.',
  'unsub.done_body': 'You won’t get marketing email from us any more. Invoices and service emails still arrive.',

  // PayPal buttons and the Turnstile check
  'pay.paypal_failed': 'PayPal could not take the payment.',
  'pay.paypal_blocked': 'PayPal’s buttons didn’t load here. You can still approve the payment on PayPal’s site.',
  'pay.paypal_continue': 'Continue to PayPal',
  'turnstile.failed': 'Could not load the security check. Reload the page and try again.',

  // Tables (web/src/components/ui.jsx)
  'table.select_all': 'Select all on this page',
  'table.select_row': 'Select {name}',

  // Step indicators (web/src/components/wizard.jsx)
  'steps.label': 'Steps',
  'steps.done': '(done)',

  // Contact invitations (clients module: invites.jsx)
  'invite_status.pending': 'Waiting',
  'invite_status.expired': 'Expired',
  'invite_status.revoked': 'Withdrawn',
  'invites.email_help': 'They get a link to choose their name and password. It works once, for 7 days.',
  'invites.may_do': 'What they may do when logged in',
  'invites.send': 'Send invitation',
  'invites.resent': 'Sent the invitation to {email} again.',
  'invites.withdrawn': 'Invitation withdrawn: its link no longer works.',
  'invites.title': 'Invitations',
  'invites.lead': 'Invite someone by email: they choose their own name and password and get the permissions you pick.',
  'invites.invite': 'Invite by email',
  'invites.permissions': 'Permissions',
  'invites.sent': 'Sent',
  'invites.actions_for': 'Actions for the invitation to {email}',
  'invites.send_again': 'Send again',
  'invites.withdraw_menu': 'Withdraw…',
  'invites.withdraw_title': 'Withdraw the invitation to {email}?',
  'invites.withdraw_body': 'The link in their email stops working.',
  'invites.withdraw': 'Withdraw',
  'invites.none': 'No open invitations.',
  'invites.dialog': 'Invite a contact',
  'invites.sent_to': 'Invitation sent to {email}.',
  'invites.invitation': 'Invitation',
  'invites.invalid':
    'This invitation has expired, was withdrawn or was already used. Ask the person who invited you to send it again.',
  'invites.done': 'You’re in',
  'invites.done_body': 'Your login is ready. Log in with {email} and the password you just chose.',
  'invites.join': 'Join {account}',
  'invites.join_account': 'Join the account',
  'invites.by_to': '{name} invited you to log in as {email}, to: {list}.',
  'invites.by_emails': '{name} invited you to log in as {email} to get the account’s emails.',
  'invites.anon_to': 'You were invited to log in as {email}, to: {list}.',
  'invites.anon_emails': 'You were invited to log in as {email} to get the account’s emails.',
  'invites.accept': 'Accept the invitation',
};

/** Fills `{name}` placeholders. Unknown ids come back as the id so gaps are visible, not blank. */
export function format(messages, id, vars = {}) {
  const text = messages[id] ?? MESSAGES[id] ?? id;
  return text.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));
}
